import React, { useEffect, useRef, useState } from 'react';

export default function AdModal({ isOpen, onClose, onAdLoaded }) {
  const adRef = useRef(null);
  const isDevelopment = import.meta.env.DEV;
  const [hasLoaded, setHasLoaded] = useState(false);
  const [countdown, setCountdown] = useState(10);
  const [loadingText, setLoadingText] = useState('Loading ad...');
  const [adError, setAdError] = useState(false);
  const adInitializedRef = useRef(false);

  // Reset states when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setHasLoaded(false);
      setCountdown(10);
      setLoadingText('Loading ad...');
      setAdError(false);
      adInitializedRef.current = false;
      return;
    }
  }, [isOpen]);

  // Handle ad initialization
  useEffect(() => {
    if (!isOpen || !adRef.current || hasLoaded || adInitializedRef.current) return;

    const initializeAd = async () => {
      try {
        // Clear any existing content
        adRef.current.innerHTML = '';
        
        if (isDevelopment) {
          const testAd = document.createElement('div');
          testAd.style.width = '300px';
          testAd.style.height = '250px';
          testAd.style.backgroundColor = '#f0f0f0';
          testAd.style.border = '1px solid #ccc';
          testAd.style.display = 'flex';
          testAd.style.alignItems = 'center';
          testAd.style.justifyContent = 'center';
          testAd.innerHTML = '<div class="text-center"><div class="text-lg font-medium mb-2">Test Ad</div><div class="text-sm text-gray-500">Development Mode</div></div>';
          adRef.current.appendChild(testAd);
          adInitializedRef.current = true;
        } else {
          // Wait for AdSense to be ready
          if (typeof window.adsbygoogle === 'undefined') {
            throw new Error('AdSense not loaded');
          }

          // Create container with fixed size
          const adContainer = document.createElement('div');
          adContainer.style.width = '300px';
          adContainer.style.height = '250px';
          adContainer.style.margin = '0 auto';
          adRef.current.appendChild(adContainer);

          // Create the ad with fixed size
          const adElement = document.createElement('ins');
          adElement.className = 'adsbygoogle';
          adElement.style.display = 'inline-block';
          adElement.style.width = '300px';
          adElement.style.height = '250px';
          adElement.dataset.adClient = "ca-pub-9534818731331315";
          adElement.dataset.adSlot = "8064583076";
          adElement.dataset.adFormat = "auto";
          adElement.dataset.fullWidthResponsive = "true";
          
          // Add target="_blank" to the container to open in new window
          adContainer.innerHTML = '<a target="_blank" rel="noopener noreferrer"></a>';
          adContainer.firstChild.appendChild(adElement);
          
          // Small delay to ensure the container is properly rendered
          await new Promise(resolve => setTimeout(resolve, 100));
          
          try {
            await (window.adsbygoogle = window.adsbygoogle || []).push({});
            adInitializedRef.current = true;
          } catch (pushError) {
            console.error('Error pushing ad:', pushError);
            throw pushError;
          }
        }
      } catch (error) {
        console.error('Error loading ad:', error);
        setAdError(true);
        setLoadingText('Error loading ad. Please try again.');
      }
    };

    // Initialize the ad
    initializeAd();
  }, [isOpen, isDevelopment, hasLoaded]);

  // Handle countdown
  useEffect(() => {
    if (!isOpen || hasLoaded || !adInitializedRef.current) return;

    const timer = setInterval(() => {
      setCountdown(prev => {
        const newCount = prev - 1;
        if (newCount >= 0) {
          setLoadingText(`You can continue in ${newCount} seconds...`);
        }
        if (newCount === 0) {
          clearInterval(timer);
          setLoadingText('Thank you for supporting Content Creator AI!');
          setHasLoaded(true);
          onAdLoaded?.();
        }
        return Math.max(0, newCount);
      });
    }, 925);

    return () => clearInterval(timer);
  }, [isOpen, onAdLoaded, hasLoaded]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-4 rounded-lg shadow-lg max-w-md w-full">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Supporting Content Creator AI</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-xl font-bold"
            type="button"
          >
            ×
          </button>
        </div>
        <div 
          ref={adRef}
          className="flex justify-center items-center w-full"
        />
        <div className="mt-4 text-center">
          <div className="text-sm text-gray-600 mb-2">
            {loadingText}
          </div>
          {!adError && (
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-1000" 
                style={{ width: `${(countdown / 10) * 100}%` }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
